import React, { useEffect, useState } from 'react';
import { Bookmark, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { ThematicSessionTemplate } from '@/pages/reports/utils/thematicSessionTemplates';

interface ThematicTemplatePanelProps {
  thematicTemplates: ThematicSessionTemplate[];
  selectedTemplateId: string;
  onTemplateSelect: (_id: string) => void;
  onSaveTemplate: (_name: string) => void;
  onUpdateTemplate: (_name: string) => void;
  onDeleteTemplate: () => void;
}

export const ThematicTemplatePanel: React.FC<ThematicTemplatePanelProps> = ({
  thematicTemplates,
  selectedTemplateId,
  onTemplateSelect,
  onSaveTemplate,
  onUpdateTemplate,
  onDeleteTemplate,
}) => {
  const [templateName, setTemplateName] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    if (selectedTemplateId) {
      const found = thematicTemplates.find((t) => t.id === selectedTemplateId);
      if (found) setTemplateName(found.name);
    }
  }, [selectedTemplateId, thematicTemplates]);

  const selectedTemplate = thematicTemplates.find((t) => t.id === selectedTemplateId);
  const trimmedName = templateName.trim();

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
      <div
        className="px-4 py-3 border-b border-gray-200 bg-gray-100 flex items-center justify-between cursor-pointer"
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
      >
        <div className="flex items-center gap-2">
          <Bookmark className="w-4 h-4 text-gray-600" />
          <p className="text-sm font-semibold text-gray-800">Modelos reutilizáveis</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-0' : '-rotate-90'}`} />
      </div>

      {isOpen && (
        <div className="p-4 space-y-6">

          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Carregar modelo existente
            </p>
            <p className="text-sm text-gray-600">
              Selecione um modelo salvo para preencher os campos automaticamente.
            </p>

            {thematicTemplates.length === 0 ? (
              <div className="rounded-md border border-dashed border-gray-300 bg-white px-4 py-5">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-4 h-4 text-gray-400" />
                  <p className="text-sm font-medium text-gray-700">Nenhum modelo salvo</p>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Salve a configuracao atual em "Salvar como modelo" para reutilizar depois.
                </p>
              </div>
            ) : (
              <select
                className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white"
                value={selectedTemplateId}
                onChange={(e) => onTemplateSelect(e.target.value)}
              >
                <option value="">Nenhum modelo</option>
                {thematicTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="border-t border-gray-200" />

          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Salvar como modelo
            </p>
            <p className="text-sm text-gray-600">
              Dê um nome para identificar este modelo. O nome é independente
              do título da sessão.
            </p>

            <input
              type="text"
              className="w-full h-10 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-400"
              placeholder="Ex: BBB 26, Copa do Mundo 2026..."
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
            />

            {!trimmedName && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Preencha o nome acima para habilitar o salvamento.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onSaveTemplate(trimmedName)}
                disabled={!trimmedName}
              >
                Salvar como novo modelo
              </Button>

              {selectedTemplate && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onUpdateTemplate(trimmedName)}
                    disabled={!trimmedName}
                  >
                    Atualizar "{selectedTemplate.name}"
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Excluir modelo
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle>Excluir modelo</DialogTitle>
            <DialogDescription>
              Tem certeza que deseja excluir o modelo &quot;{selectedTemplate?.name}&quot;? Esta acao nao pode ser desfeita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-end gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => {
                onDeleteTemplate();
                setDeleteDialogOpen(false);
              }}
            >
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
